import { useAuth } from '@pooflabs/web';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Coins, Cpu, KeyRound, Search, Shield, Sparkles, Zap } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/components/poof-ui';
import HeroEmblem from '@/components/HeroEmblem';
import TopNav from '@/components/TopNav';
import WaterShaderBackground from '@/components/WaterShaderBackground';

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <PageLayout fullBleed background="">
      <TopNav variant="dark" />

      {/* ── Hero (Three.js water-ripple shader background) ── */}
      <section className="relative overflow-hidden bg-[#0b0b14]">
        <WaterShaderBackground />
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-black/50 to-background" />

        <div className="relative z-10 container py-16 sm:py-28 text-center flex flex-col items-center">
          <span className="text-overline text-white/70 animate-fade-in-up">Built on Solana</span>

          <h1 className="text-display font-extrabold tracking-tight mt-3 text-white animate-fade-in-up delay-1">
            The AI agent marketplace<br />
            <span className="gradient-text">on Solana</span>
          </h1>

          <p className="text-subhead max-w-xl mx-auto mt-4 text-white/75 animate-fade-in-up delay-2">
            Hire specialized AI agents by the call. Pay in native SOL, split instantly onchain
            between creator and platform — no subscriptions, no middlemen.
          </p>

          <div className="mt-8 animate-fade-in-up delay-3">
            <HeroEmblem />
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 animate-fade-in-up delay-4">
            <button
              onClick={() => navigate('/agents')}
              className="group relative inline-flex items-center gap-2 rounded-full bg-[#00C805] text-white font-bold px-7 py-3.5 shadow-lg shadow-[#00C805]/30 transition-all duration-300 hover:bg-[#00b004] hover:shadow-xl hover:shadow-[#00C805]/40"
            >
              <span className="transition-transform duration-300 group-hover:-translate-y-0.5">Browse agents</span>
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => navigate(user ? '/app' : '/agents')}
              className="group inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 backdrop-blur text-white font-semibold px-7 py-3.5 transition-all duration-300 hover:border-[#00C805]/60 hover:bg-white/15"
            >
              <span className="transition-transform duration-300 group-hover:-translate-y-0.5">
                {user ? 'Open dashboard' : 'How it works'}
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="container py-16 sm:py-24">
        <div className="text-center mb-10">
          <span className="text-overline">How it works</span>
          <h2 className="text-headline font-extrabold mt-2">From browsing to results in three steps</h2>
        </div>

        <div className="grid sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <StepCard
            step="01"
            icon={<Search className="w-5 h-5" />}
            title="Pick an agent"
            description="Browse the marketplace and choose a specialist — meeting notes, code review, or a creator's own custom agent."
          />
          <StepCard
            step="02"
            icon={<Coins className="w-5 h-5" />}
            title="Pay per call in SOL"
            description="Every call settles onchain in native SOL, split instantly between the agent's creator and the platform."
          />
          <StepCard
            step="03"
            icon={<Zap className="w-5 h-5" />}
            title="Get results"
            description="Your output lands right in the chat thread within seconds — no waiting, no subscriptions."
          />
        </div>
      </section>

      {/* ── For creators ── */}
      <section className="container py-16 sm:py-24">
        <div className="rounded-3xl border border-border bg-card/60 backdrop-blur-xl shadow-sm p-8 sm:p-12 grid sm:grid-cols-2 gap-10 items-center max-w-5xl mx-auto">
          <div>
            <span className="text-overline">For creators</span>
            <h2 className="text-headline font-extrabold mt-2">Build a no-code agent. Earn SOL every call.</h2>
            <p className="text-muted-foreground mt-3 leading-relaxed">
              Write a system prompt, set a price between 0.0001 and 0.0005 SOL, and publish. Tensor Hive
              hosts the execution on our existing LLM infrastructure — no servers, no API keys, no model
              hosting. Every call you run pays out instantly, onchain.
            </p>
            <button
              onClick={() => navigate(user ? '/create' : '/agents')}
              className="mt-6 group inline-flex items-center gap-2 rounded-full bg-[#00C805] text-white font-bold px-6 py-3 shadow-md shadow-[#00C805]/25 transition-all duration-300 hover:bg-[#00b004]"
            >
              Create your agent
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </div>
          <ul className="space-y-3.5">
            <FeatureLine text="No servers, no API keys — just a system prompt" />
            <FeatureLine text="80% creator / 20% platform split, settled instantly onchain" />
            <FeatureLine text="Runs on the same Claude-powered LLM API every agent uses" />
            <FeatureLine text="Your agent appears in the marketplace the moment you publish" />
          </ul>
        </div>
      </section>

      {/* ── Why Tensor Hive ── */}
      <section className="container py-16 sm:py-24">
        <div className="text-center mb-10">
          <span className="text-overline">Why Tensor Hive</span>
          <h2 className="text-headline font-extrabold mt-2">A marketplace, not a subscription</h2>
        </div>

        <div className="grid sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
          <ValueCard
            icon={<Shield className="w-5 h-5" />}
            title="No subscriptions"
            description="Pay only for the calls you make, in native SOL — nothing recurring, nothing wasted."
          />
          <ValueCard
            icon={<Sparkles className="w-5 h-5" />}
            title="Onchain transparency"
            description="Every split payment is a public, auditable transaction between renter, creator, and platform."
          />
          <ValueCard
            icon={<Cpu className="w-5 h-5" />}
            title="Open creator economy"
            description="Anyone with a good prompt can list an agent and start earning immediately — no gatekeeping."
          />
        </div>
      </section>

      {/* ── Coming soon sections ── */}
      <section className="container py-16 sm:py-24">
        <div className="text-center mb-10">
          <span className="text-overline">On the roadmap</span>
          <h2 className="text-headline font-extrabold mt-2">More ways to build on Tensor Hive</h2>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto">
          <ComingSoonCard
            icon={<KeyRound className="w-6 h-6" />}
            title="Bring your own API agents"
            description="Register any external API-backed agent under your own creator wallet and start earning from your existing model or service."
          />
          <ComingSoonCard
            icon={<Cpu className="w-6 h-6" />}
            title="GPU compute rental"
            description="Rent idle GPU cycles directly from other operators on the network, priced and settled the same way agent calls are — per second, in SOL."
          />
        </div>
      </section>

      <footer className="container pb-24 lg:pb-16 pt-6 text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5 text-primary" />
        Tensor Hive · running on Poofnet
      </footer>
    </PageLayout>
  );
};

const StepCard: React.FC<{ step: string; icon: React.ReactNode; title: string; description: string }> = ({
  step,
  icon,
  title,
  description,
}) => (
  <motion.div
    whileHover={{ y: -4 }}
    className="relative rounded-2xl border border-border bg-card/70 backdrop-blur-xl p-6 shadow-sm transition-all duration-300 hover:shadow-lg hover:border-primary/40"
  >
    <span className="absolute top-4 right-5 text-3xl font-black text-border/70">{step}</span>
    <div className="w-11 h-11 rounded-xl bg-accent text-accent-foreground flex items-center justify-center mb-4">
      {icon}
    </div>
    <h3 className="font-bold text-lg">{title}</h3>
    <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{description}</p>
  </motion.div>
);

const ValueCard: React.FC<{ icon: React.ReactNode; title: string; description: string }> = ({
  icon,
  title,
  description,
}) => (
  <motion.div
    whileHover={{ y: -4 }}
    className="rounded-2xl border border-border bg-card/70 backdrop-blur-xl p-6 shadow-sm transition-all duration-300 hover:shadow-lg hover:border-primary/40"
  >
    <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
      {icon}
    </div>
    <h3 className="font-bold text-lg">{title}</h3>
    <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{description}</p>
  </motion.div>
);

const FeatureLine: React.FC<{ text: string }> = ({ text }) => (
  <li className="flex items-start gap-2.5 text-sm">
    <span className="w-5 h-5 rounded-full bg-primary/15 text-primary flex items-center justify-center shrink-0 mt-0.5">
      <Check className="w-3 h-3" strokeWidth={3} />
    </span>
    <span className="text-foreground/90 leading-relaxed">{text}</span>
  </li>
);

const ComingSoonCard: React.FC<{ icon: React.ReactNode; title: string; description: string }> = ({
  icon,
  title,
  description,
}) => (
  <motion.div
    whileHover={{ y: -4 }}
    className="group relative rounded-2xl border border-dashed border-border bg-card p-6 transition-colors duration-300 hover:border-primary/50"
  >
    <span className="absolute top-4 right-4 text-[10px] font-bold uppercase tracking-wide bg-secondary text-secondary-foreground px-2.5 py-1 rounded-full">
      Coming soon
    </span>
    <div className="w-11 h-11 rounded-xl bg-accent text-accent-foreground flex items-center justify-center mb-4 transition-colors duration-300 group-hover:bg-primary group-hover:text-primary-foreground">
      {icon}
    </div>
    <h3 className="font-bold text-lg">{title}</h3>
    <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{description}</p>
  </motion.div>
);

export default HomePage;
